'use client';

import { createContext, useContext, useState } from 'react';
import type { Option } from '../../../form-controls/types';

type NotificationFunction = (gameName: string) => boolean;

type ResultsInteractionContextValue = {
  selectedOption: Option<string> | null;
  setSelectedOption: (option: Option<string>) => void;
  shouldShowNotification: NotificationFunction;
};

const ResultsInteractionContext = createContext<ResultsInteractionContextValue | null>(null);

function ResultsInteractionProvider({ children, shouldShowNotification }: { children: React.ReactNode; shouldShowNotification?: NotificationFunction }) {
  const [selectedOption, setSelectedOption] = useState<Option<string> | null>(null);

  const defaultNotificationFn: NotificationFunction = () => false;

  const value: ResultsInteractionContextValue = {
    selectedOption,
    setSelectedOption,
    shouldShowNotification: shouldShowNotification ?? defaultNotificationFn,
  };

  return <ResultsInteractionContext.Provider value={value}>{children}</ResultsInteractionContext.Provider>;
}

function useResultsInteraction() {
  const context = useContext(ResultsInteractionContext);
  if (!context) {
    throw new Error('useResultsInteraction must be used within ResultsInteractionProvider');
  }
  return context;
}

export { ResultsInteractionContext, ResultsInteractionProvider, useResultsInteraction };
export type { NotificationFunction, ResultsInteractionContextValue };
